import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Box, Button, CircularProgress, InputBase, Typography, Modal } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";

import CustomAxios from "../../utils/CustomAxios";

import {
  updateModal,
  updateInstructionEdit,
  setCurrentInstruction,
  updateSearchInstruction,    
} from "../../Redux/add-instruction";
import { AddInstructions_table } from "../../sub_components/add_instructions_table";
import { useGetCategories, useGetInstructions, useGetSubCategories } from "../globalFunctions";
import { MultiSelect } from "primereact/multiselect";


export function AddInstructions() {
  const dispatch = useDispatch();

  // Redux state
  const InstructionModal  = useSelector((s) => s.addInstruction.modal);
  const InstructionEdit   = useSelector((s) => s.addInstruction.instructionEdit);
  const current           = useSelector((s) => s.addInstruction.current);     // <— the row being edited
  const actcategory       = useSelector((s) => s.category.category);
  const products = useSelector(state => state.products.products)
  const user_role = localStorage.getItem('role');
  
  // Get assigned hubs for Customer Engagement Admin
  const assignedHubs = JSON.parse(localStorage.getItem('hub') || '[]');
  
  // Filter products to show only from assigned hubs for Customer Engagement Admin
  const filteredProducts = (products || []).filter((product) => {
    if (user_role === 'customer_engagement_admin') {
      return assignedHubs.includes(product.inventory_id);
    }
    return true; 
  });

  // data loaders (hooks return functions)
  const getSubCategory  = useGetSubCategories();
  const getCategory     = useGetCategories();
  const getInstructions = useGetInstructions();

  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  // always-controlled empty form
  const EMPTY_FORM = { instruction_name: "", product_ids: [] };
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Load data on mount / auth change
  const authToken = localStorage.getItem("authtoken");
  useEffect(() => {
    getCategory();
    getSubCategory();
    getInstructions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);


    useEffect(() => {
      if (current) {
        console.log('------------------current----->>>>>>>>>', current)
        setFormData({
        instruction_name: current.instruction_name ?? "",
        product_ids: current.product_id,
        });
    } else {
        setFormData({ instruction_name: "",  product_ids: [] });
    }
    }, [current, InstructionModal]);


// Controlled inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  // Open modal in "add" mode
  const handleOpen = () => {
    setErrors({});
    setFormData(EMPTY_FORM);
    dispatch(setCurrentInstruction(null));  // ensure not editing
    dispatch(updateInstructionEdit(false)); // add mode
    dispatch(updateModal(true));
  };

  // Close modal safely
  const handleModalClose = () => {
    setErrors({});
    dispatch(updateModal(false));
    dispatch(updateInstructionEdit(false));
    dispatch(setCurrentInstruction(null));  // clear the selected row
    setFormData(EMPTY_FORM);
  };

  const validationSchema = Yup.object().shape({
    instruction_name: Yup.string().required("Instruction Name is required"),
    // instruction_category: Yup.mixed().required("Category is required"),
  });

  // Normalize category payload (id vs object)
  const catPayload =
    formData.instruction_category &&
    (formData.instruction_category.id ?? formData.instruction_category);

  const handleSaveInstruction = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setLoading(true);

      await CustomAxios.post("instructions/", {
        instruction_name: formData.instruction_name,
        product_id: formData.product_ids,
      });

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Instruction added successfully",
        life: 3000,
      });

      await getInstructions(); // refresh list
      handleModalClose();
    } catch (error) {
      if (error.name === "ValidationError") {
        const formErrors = {};
        error.inner.forEach((err) => (formErrors[err.path] = err.message));
        setErrors(formErrors);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Something went wrong",
          life: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInstruction = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setLoading(true);

      const id = current?.instruction_id ?? current?.id;
      await CustomAxios.put(`instructions/${id}/`, {
        instruction_name: formData.instruction_name,
        product_id: formData.product_ids,
      });

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Instruction updated successfully",
        life: 3000,
      });

      await getInstructions(); // refresh list
      handleModalClose();
    } catch (error) {
      if (error.name === "ValidationError") {
        const formErrors = {};
        error.inner.forEach((err) => (formErrors[err.path] = err.message));
        setErrors(formErrors);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Update failed",
          life: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };
    const countryOptionTemplate = (option) => {
        return (
            <Box sx={{ display: "flex", gap: "10px" }}>
                <Box component="img" alt={option.name} src={option.image} sx={{ width: '30px', height: "30px", objectFit: "cover" }} />
                <Box>{option.name}</Box>
            </Box>
        );
    };
    const selectedCountryTemplate = (option, props) => {
        if (option) {
            return (
                <Box sx={{ display: "flex", gap: "10px" }}>
                    <Box component="img" alt={option.name} src={option.image} sx={{ width: '16px', height: "16px", objectFit: "cover" }} />
                    <Box>{option.name}</Box>
                </Box>
            );
        }

        return <span>{props.placeholder}</span>;
    };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%", padding: "30px" }}>
      <Toast ref={toast} />

      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <Box sx={{ position: "relative" }}>
          <InputBase
            sx={{
              backgroundColor: "white",
              height: "32px",
              width: "100%",
              maxWidth: "268px",
              borderRadius: "2px",
              border: "1px solid",
              borderColor: "secondary.border",
              padding: "5px 12px 5px 40px",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "22px",
              color: "black",
            }}
            placeholder="Search item name"
          />
          <SearchIcon sx={{ position: "absolute", left: "12px", top: "5px", width: "20px", height: "20px" }} />
        </Box>

        <Button variant="contained" sx={{ textTransform: "none" }} onClick={handleOpen}>
          <Typography sx={{ fontSize: { sm: "12px", md: "14px" }, fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
            Add Instruction
          </Typography>
        </Button>
      </Box>

      {/* Table keeps same UI */}
      <AddInstructions_table />

      <Modal open={InstructionModal} onClose={handleModalClose} sx={{ zIndex: 1000 }}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "100%", maxWidth: "600px", height: "357px", backgroundColor: "white" }}>
          <Box sx={{ height: "46px important", padding: "12px 24px", backgroundColor: "#EEEEEE", display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "14", fontWeight: "500", lineHeight: "22px" }}>
              {InstructionEdit ? "Edit Category" : "Add Instructions"}
            </Typography>
          </Box>

          <Box sx={{ padding: "24px", display: "flex", gap: "16px", flexDirection: "column", height: "590px", overflowY: "auto" }}>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
              <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Instructions Name</Typography>
              <InputBase
                name="instruction_name"
                value={formData.instruction_name ?? ""}
                onChange={handleChange}
                sx={{ backgroundColor: "white", width: "100%", height: "32px", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px" }}
                placeholder="Enter Name of Instructions"
              />
              {errors.instruction_name && (
                <Typography style={{ color: "red", fontSize: "12PX", fontWeight: "400", lineHeight: "22px" }}>
                  {errors.instruction_name}
                </Typography>
              )}
            </Box>

            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                      <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Add On</Typography>
                      <MultiSelect
                          onChange={handleChange}
                          value={formData.product_ids}   
                          options={filteredProducts}
                          name="product_ids"             
                          optionLabel="item_name"  
                          style={{ zIndex: "1000", height: "32px" ,width:'100%'}}
                          placeholder="Select Product"
                          className="w-full md:w-14rem"
                          valueTemplate={selectedCountryTemplate}
                          itemTemplate={countryOptionTemplate}
                          filter filterBy="item_name"
                      />
              {errors.instruction_category && (
                <Typography style={{ color: "red", fontSize: "12PX", fontWeight: "400", lineHeight: "22px" }}>
                  {errors.instruction_category}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ backgroundColor: "#EEEEEE", display: "flex", alignItems: "center", position: "absolute", bottom: 0, justifyContent: "flex-end", width: "100%" }}>
            <Box sx={{ padding: "12px 24px", display: "flex", gap: "20px" }}>
              <Button variant="outlined" sx={{ textTransform: "none", color: "black", backgroundColor: "white", height: "32px", width: "67px" }} onClick={handleModalClose}>
                cancel
              </Button>

              <Button
                variant="contained"
                sx={{ textTransform: "none", height: "32px", width: "67px" }}
                onClick={InstructionEdit ? handleUpdateInstruction : handleSaveInstruction}
                disabled={loading}
              >
                {InstructionEdit ? (loading ? <CircularProgress size="25px" sx={{ color: "white" }} /> : "Update") : loading ? <CircularProgress size="25px" sx={{ color: "white" }} /> : "Save"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

